import lodash, { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import Button from "@/main/components/shared/button/button";
import Box from "@/main/components/shared/box";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogKeyboardDatePickerContainer from "@/main/components/shared/datepicker/keyboardDatePickerContainer";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import useFormQueryParams from "@/main/hooks/useformQueryParams";
import {
	getDefaultCountryOptionDrop,
	getDefaultOptionDrop,
	getTranslateByKey,
	isNullUndefined,
	removeEmptyValues,
} from "@/main/services/utils";
import { AreaButton } from "@/main/components/shared/styles";

import { procurementReportApi } from "@/main/api";
import { KeyValue } from "@/main/models/common";
import { BodyShopFilterRequest, InsurerFilterRequest } from "@/main/models/request";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import {
	formatDateToISOString,
	getKeysIgnoreValidationsDate,
	initalDateForm,
	onLoadDateRangePicker,
	validateFormDate,
} from "@/main/services/utilsDate";
import { downloadFile } from "@/main/services/utilsFile";
import { userIsBodyshop, userIsInsurer } from "@/main/services/utilsUser";
import { getBodyshops, resetReducerBodyshops, useBodyshopReducer } from "@/main/store/bodyshop";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getInsurers, resetReducerInsurers, useInsurerReducer } from "@/main/store/insurer";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";

import * as S from "./styles";
import { ExportRequoteTraceabilityRequest, SearchGridRequoteTraceabilityRequest } from "@/main/models/request/";
import { getDomainQuoteStatus, resetReducerQuoteStatus, useQuoteStatusReducer } from "@/main/store/quoteStatus";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";

interface IFiltersRequoteTraceabilityProps {
	showFilters: boolean;
	formFilters: SearchGridRequoteTraceabilityRequest | null;
	setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
	onResetSearch: () => void;
	getDataGrid: (filters: SearchGridRequoteTraceabilityRequest) => void;
}

function FiltersRequoteTraceability({
	showFilters,
	getDataGrid,
	onResetSearch,
	setShowFilters,
}: IFiltersRequoteTraceabilityProps) {
	const parameterRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.DefaultRequoteReportDaysInterval);
	const parameterLengthFilterClaim = useParameterByTypeScreenReducer(
		ParameterTypeEnum.RequoteTraceabilityScreenFilterClaimLength,
	);
	// #region Form constants
	const INITIAL_FORM_STATE = {
		country: null as KeyValue | null,
		insurer: null as KeyValue | null,
		bodyshop: null as KeyValue | null,
		quoteStatus: null as KeyValue | null,
		initialDate: null as Date | null,
		finalDate: null as Date | null,
		claimNr: "",
	};
	type IFormFiltersRequoteTraceability = typeof INITIAL_FORM_STATE;
	//#endregion

	// #region Reducers/Options
	const { countriesUser: countryReducer } = useCountryReducer();
	const { insurers: insurerReducer } = useInsurerReducer();
	const { bodyshops: bodyshopReducer } = useBodyshopReducer();
	const { quoteStatus: quoteStatusReducer } = useQuoteStatusReducer();
	// #endregion

	// #region Hooks
	const dispatch = useDispatch();
	const methods = useFormQueryParams<IFormFiltersRequoteTraceability>({
		defaultValues: INITIAL_FORM_STATE,
		onSubmit: onSubmitFilter,
		mapDefaultValues: {
			country: countryReducer,
			bodyshop: bodyshopReducer,
			insurer: insurerReducer,
			quoteStatus: quoteStatusReducer,
		},
	});
	const { control, reset, getValues, setValue, customHandleSubmit, queryParamsValues, clearErrors, watch } = methods;
	//#endregion

	// #region States/Watch
	const optionDefaultCountryDrop = getDefaultOptionDrop(countryReducer.data);
	const optionDefaultInsurerDrop = userIsInsurer() ? getDefaultOptionDrop(insurerReducer.data) : null;
	const [isIgnoreValidationsDate, setIsIgnoreValidationsDate] = useState<boolean>(false);
	// #endregion

	// #region Async Functions
	async function onSubmitFilter(data: IFormFiltersRequoteTraceability, isQueryParams: boolean = false) {
		const ignoreValidationsDateSubmit = data.claimNr.length >= parameterLengthFilterClaim;
		const keysIgnoreValidationsDateSubmit = getKeysIgnoreValidationsDate(
			ignoreValidationsDateSubmit,
			data.initialDate,
			data.finalDate,
		);

		if (isQueryParams) {
			if (isNullUndefined(data.initialDate)) {
				const valueStartDate = ignoreValidationsDateSubmit ? null : initalDateForm(parameterRangeDays);

				data.initialDate = valueStartDate;
				setValue("initialDate", valueStartDate);
			}
			if (isNullUndefined(data.finalDate)) {
				const valueFinalDate = ignoreValidationsDateSubmit ? null : initalDateForm();

				data.finalDate = valueFinalDate;
				setValue("finalDate", valueFinalDate);
			}
		}
		validateFormDate(data.initialDate, data.finalDate, parameterRangeDays, keysIgnoreValidationsDateSubmit);

		if (isNullUndefined(data.country)) return;
		const request: SearchGridRequoteTraceabilityRequest = {
			guidCountry: String(data.country.key),
			guidInsurer: data.insurer?.key ? String(data.insurer.key) : null,
			guidBodyShop: data.bodyshop?.key ? String(data.bodyshop.key) : null,
			initialDate: isNullUndefined(data.initialDate) ? "" : formatDateToISOString(data.initialDate),
			finalDate: isNullUndefined(data.finalDate) ? "" : formatDateToISOString(data.finalDate),
			IdQuoteStatus: data.quoteStatus?.key ? Number(data.quoteStatus.key) : null,
			claimNumber: data.claimNr,
		};
		getDataGrid(removeEmptyValues(request));
		setShowFilters(false);
	}

	function constructInitialRequest(): IFormFiltersRequoteTraceability {
		const request: IFormFiltersRequoteTraceability = cloneDeep(INITIAL_FORM_STATE);
		const { firstDateResult, secondDateResult } = onLoadDateRangePicker(parameterRangeDays);
		setValue("initialDate", firstDateResult);
		setValue("finalDate", secondDateResult);
		request.initialDate = firstDateResult;
		request.finalDate = secondDateResult;
		return cloneDeep(request);
	}
	// #endregion

	// #region Handle Functions
	function handleClear() {
		reset(INITIAL_FORM_STATE);
		setValue("country", optionDefaultCountryDrop);
		const { firstDateResult, secondDateResult } = onLoadDateRangePicker(parameterRangeDays);
		setValue("initialDate", firstDateResult);
		setValue("finalDate", secondDateResult);
		callInsurerApiDomain();
		callBodyshopApiDomain();
		callQuoteStatusApiDomain();
		onResetSearch();
	}

	function checkValidationDates(inputClaimNR: string) {
		if (inputClaimNR.length >= parameterLengthFilterClaim && !isIgnoreValidationsDate) {
			setIsIgnoreValidationsDate(true);
			return true;
		}
		if (inputClaimNR.length < parameterLengthFilterClaim && isIgnoreValidationsDate) {
			setIsIgnoreValidationsDate(false);
			return false;
		}
		return null;
	}

	function handleIgnoreValidationsDate(isQueryParams: boolean = false) {
		const valueInitialDate =
			isQueryParams && !isNullUndefined(queryParamsValues.initialDate) ? queryParamsValues.initialDate : null;
		const valueFinalDate =
			isQueryParams && !isNullUndefined(queryParamsValues.finalDate) ? queryParamsValues.finalDate : null;

		setValue("initialDate", valueInitialDate);
		setValue("finalDate", valueFinalDate);
	}

	function handleAcceptDateValidations(isQueryParams: boolean = false) {
		const { firstDateResult, secondDateResult } = onLoadDateRangePicker(parameterRangeDays);
		const valueInitialDate =
			isQueryParams && !isNullUndefined(queryParamsValues.initialDate)
				? queryParamsValues.initialDate
				: firstDateResult;
		const valueFinalDate =
			isQueryParams && !isNullUndefined(queryParamsValues.finalDate) ? queryParamsValues.finalDate : secondDateResult;

		setValue("initialDate", valueInitialDate);
		setValue("finalDate", valueFinalDate);
		clearErrors(["initialDate", "finalDate"]);
	}
	// #endregion

	// #region Call reducers function
	function callInsurerApiDomain(companySelected: KeyValue | null = null) {
		// Clean fields & reducers
		dispatch(resetReducerInsurers());

		if (optionDefaultCountryDrop !== null) {
			const request: InsurerFilterRequest = {
				...(companySelected && { guidBodyShop: String(companySelected.key) }),
				guidCountry: String(optionDefaultCountryDrop?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};

			dispatch(getInsurers(request));
		}
	}

	function callBodyshopApiDomain(insurerSelected: KeyValue | null = null) {
		// Clean fields & reducers
		dispatch(resetReducerBodyshops());
		if (optionDefaultCountryDrop !== null) {
			const request: BodyShopFilterRequest = {
				...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
				guidCountry: String(optionDefaultCountryDrop?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getBodyshops(request));
		}
	}

	function callQuoteStatusApiDomain() {
		dispatch(resetReducerQuoteStatus());

		dispatch(getDomainQuoteStatus());
	}

	//#endregion

	// #region UseEffects
	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
		constructInitialRequest();
		return () => {
			dispatch(resetReducerBodyshops());
			dispatch(resetReducerInsurers());
		};
	}, []);

	useEffect(() => {
		callInsurerApiDomain();
		callBodyshopApiDomain();
		callQuoteStatusApiDomain();
	}, [optionDefaultCountryDrop?.key]);

	useEffect(() => {
		if (optionDefaultInsurerDrop?.key != null) {
			callBodyshopApiDomain(optionDefaultInsurerDrop);
		}
	}, [optionDefaultInsurerDrop?.key]);
	// #endregion
	const hasBodyshopDefault = userIsBodyshop() && bodyshopReducer?.data?.length === 1;

	const disabledCountry = isNullUndefined(countryReducer.data) || countryReducer?.data?.length === 1;
	const disabledInsurer = userIsInsurer();
	const disabledBodyshop = isNullUndefined(bodyshopReducer.data) || hasBodyshopDefault;
	return (
		<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
			<form onSubmit={customHandleSubmit} noValidate>
				<S.Container>
					<KogAutoCompleteControlled
						id="id-mappingCodes-select-country"
						nameCustom="country"
						label={getTranslateByKey("information.country")}
						isDefaultValueUnique
						valueDefaultKey={optionDefaultCountryDrop?.key}
						control={control}
						rulesCustom={{ required: true }}
						setValueCustom={setValue}
						options={countryReducer.data}
						isLoading={countryReducer.isLoading}
						disabled={disabledCountry}
						onChangeCustom={() => {}}
					/>
					<KogAutoCompleteControlled
						nameCustom="insurer"
						id="id-searchOrder-select-insurer"
						isDefaultValueUnique={optionDefaultInsurerDrop !== null}
						setValueCustom={setValue}
						label={getTranslateByKey("information.insurer")}
						control={control}
						rulesCustom={{ required: false }}
						errorMessageCustom=""
						hasErrorCustom={false}
						options={insurerReducer.data}
						isLoading={insurerReducer.isLoading}
						disabled={disabledInsurer}
						onChangeCustom={(optionSelected: KeyValue | null) => {
							if (optionSelected === null) {
								const bodyshop = getValues().bodyshop;
								if (bodyshop != null) {
									callInsurerApiDomain(bodyshop);
								} else {
									callInsurerApiDomain();
								}
							}
							callBodyshopApiDomain(optionSelected);
						}}
					/>

					<KogAutoCompleteControlled
						nameCustom="bodyshop"
						id="id-searchOrder-select-bodyshop"
						isDefaultValueUnique={disabledBodyshop}
						setValueCustom={setValue}
						label={getTranslateByKey("information.bodyshopBranch")}
						control={control}
						rulesCustom={{ required: false }}
						options={bodyshopReducer.data}
						isLoading={bodyshopReducer.isLoading}
						disabled={disabledBodyshop}
						onChangeCustom={(optionSelected: KeyValue | null) => {
							const isTypeCompanyUserInsurer = userIsInsurer();
							if (optionSelected === null) {
								if (optionDefaultInsurerDrop != null) {
									callBodyshopApiDomain(optionDefaultInsurerDrop);
								} else {
									callBodyshopApiDomain();
								}
							}
							if (!isTypeCompanyUserInsurer) callInsurerApiDomain(optionSelected);
						}}
					/>

					<KogAutoCompleteControlled
						nameCustom="quoteStatus"
						id="id-searchOrder-quote-status"
						isDefaultValueUnique={disabledBodyshop}
						setValueCustom={setValue}
						label={getTranslateByKey("information.quoteStatus")}
						control={control}
						rulesCustom={{ required: false }}
						options={quoteStatusReducer.data}
						isLoading={quoteStatusReducer.isLoading}
						disabled={false}
						onChangeCustom={() => {}}
					/>

					<KogKeyboardDatePickerContainer
						idScreen="id-requoteTraceability"
						nameStartDate="initialDate"
						nameFinalDate="finalDate"
						parameterRangeDays={parameterRangeDays}
						control={control}
						ignoreKeyValidations={getKeysIgnoreValidationsDate(
							isIgnoreValidationsDate,
							watch("initialDate"),
							watch("finalDate"),
						)}
					/>

					<KogInputTextControlledNew
						name="claimNr"
						control={control}
						label={getTranslateByKey("information.claimNr")}
						onChangeCustom={(value) => {
							const isIgnoreValidationDate = checkValidationDates(value);
							if (!isNullUndefined(isIgnoreValidationDate)) {
								if (isIgnoreValidationDate) {
									handleIgnoreValidationsDate();
								} else {
									handleAcceptDateValidations();
								}
							}
						}}
					/>

					<S.AreaButtons>
						<SecondaryButton id="id-mappingCodes-btn-submit" type="submit">
							{getTranslateByKey("information.search")}
						</SecondaryButton>
						<SecondaryButton id="id-mappingCodes-btn-clear" type="button" onClick={handleClear}>
							{getTranslateByKey("information.clear")}
						</SecondaryButton>
					</S.AreaButtons>
				</S.Container>
			</form>
		</Box>
	);
}

function WrapperFiltersRequoteTraceability(
	props: Omit<IFiltersRequoteTraceabilityProps, "showFilters" | "setShowFilters">,
) {
	//#region States
	const parameterRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.DefaultRequoteReportDaysInterval);
	const { countriesUser: countryReducer } = useCountryReducer();
	const [showFilters, setShowFilters] = useState<boolean>(true);
	const [isLoadingExport, setIsLoadingExport] = useState<boolean>(false);
	//#endregion

	function handleFilters() {
		setShowFilters((prev) => !prev);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}
	function exportExcel() {
		setIsLoadingExport(true);
		const cloneFormFilters = lodash.cloneDeep(props.formFilters);

		const request: ExportRequoteTraceabilityRequest = {
			...cloneFormFilters,
			initialDate: isNullUndefined(cloneFormFilters?.initialDate)
				? formatDateToISOString(initalDateForm(parameterRangeDays))
				: cloneFormFilters.initialDate,
			finalDate: isNullUndefined(cloneFormFilters?.finalDate)
				? formatDateToISOString(initalDateForm())
				: cloneFormFilters.finalDate,
			guidCountry: isNullUndefined(cloneFormFilters?.guidCountry)
				? String(getDefaultCountryOptionDrop(countryReducer.data)?.key)
				: cloneFormFilters.guidCountry,
		};
		procurementReportApi
			.exportRequoteTraceability(removeEmptyValues(request))
			.then((response) => {
				if (response.data.result) {
					const base64File = response.data.result;
					const nameFile = getTranslateByKey("information.requoteTraceability");
					downloadFile(base64File, `${nameFile}.xlsx`);
				}
			})
			.finally(() => {
				setIsLoadingExport(false);
			});
	}
	const mergedProps: IFiltersRequoteTraceabilityProps = {
		...props,
		showFilters,
		setShowFilters,
	};
	return (
		<>
			<AreaButton>
				<Button
					id="id-holidays-btn-exportExcel"
					isLoading={isLoadingExport || countryReducer.isLoading}
					onClick={exportExcel}
				>
					{getTranslateByKey("information.exportExcel")}
				</Button>
				<Button onClick={handleFilters} id="id-holidays-btn-filters">
					{getTranslateByKey("information.filters")}
				</Button>
			</AreaButton>
			<FiltersRequoteTraceability {...mergedProps} />
		</>
	);
}

export default WrapperFiltersRequoteTraceability;
